import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getI18n } from '@/lib/i18n';
import { LogOut, LayoutDashboard, Building2, User, UserCog, Shield, MessageSquare, MessageCircle, Menu, X, BarChart3, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import FeedbackDialog from '@/components/FeedbackDialog';
import { useChat } from '@/contexts/ChatContext';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = getI18n(user);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { unread, toggleWidget } = useChat();

  // Закрываем сайдбар при смене страницы (для мобильной навигации).
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Блокируем скролл body при открытом overlay-sidebar на мобильных.
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [sidebarOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleNav = (path) => { setSidebarOpen(false); navigate(path); };
  const handleChat = () => { setSidebarOpen(false); toggleWidget(); };
  const handleFeedback = () => { setSidebarOpen(false); setFeedbackOpen(true); };

  const roleLabel = user?.role === 'organizer' ? t('sidebar.organizer') : t('sidebar.participant');
  const isProfile = location.pathname === '/profile';
  const isAdmin = location.pathname === '/admin';
  const isAnalytics = location.pathname === '/analytics';
  const isDashboard = !isProfile && !isAdmin && !isAnalytics;
  const navItemCls = (active) =>
    `w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm font-medium transition ${
      active ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800/70 hover:text-white'
    }`;

  const sidebarContent = (
    <>
      <div className="p-5 border-b border-slate-700/50 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="font-heading text-base font-bold tracking-tight">{t('app.name')}</span>
        </div>
        {/* Кнопка закрыть — только на мобильных, когда сайдбар оверлеем */}
        <button onClick={() => setSidebarOpen(false)}
          className="lg:hidden w-8 h-8 rounded-sm text-slate-300 hover:bg-slate-800 flex items-center justify-center"
          data-testid="sidebar-close" aria-label="Закрыть меню">
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <button onClick={() => handleNav('/dashboard')} data-testid="nav-dashboard" className={navItemCls(isDashboard)}>
          <LayoutDashboard className={`w-4 h-4 ${isDashboard ? 'text-slate-400' : 'text-slate-500'}`} />
          {t('sidebar.dashboard')}
        </button>
        <button onClick={() => handleNav('/profile')} data-testid="nav-profile" className={navItemCls(isProfile)}>
          <UserCog className={`w-4 h-4 ${isProfile ? 'text-slate-400' : 'text-slate-500'}`} />
          {t('sidebar.profile')}
        </button>
        <button onClick={handleChat} data-testid="nav-chat" className={`${navItemCls(false)} relative`}>
          <MessageCircle className="w-4 h-4 text-slate-500" />
          <span className="flex-1 text-left">{t('sidebar.chats')}</span>
          {unread > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full"
              data-testid="nav-chat-unread">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
        {user?.role === 'organizer' && (
          <button onClick={() => handleNav('/analytics')} data-testid="nav-analytics" className={navItemCls(isAnalytics)}>
            <BarChart3 className={`w-4 h-4 ${isAnalytics ? 'text-blue-300' : 'text-blue-400'}`} />
            {t('sidebar.analytics')}
          </button>
        )}
        {user?.role === 'organizer' && (
          <button
            onClick={() => { setSidebarOpen(false); window.open('https://basequote.net/planning/', '_blank', 'noopener,noreferrer'); }}
            data-testid="nav-planning"
            className={navItemCls(false)}
          >
            <CalendarClock className="w-4 h-4 text-emerald-500" />
            {t('sidebar.planning')}
          </button>
        )}
        {user?.is_super_admin && (
          <button onClick={() => handleNav('/admin')} data-testid="nav-admin" className={navItemCls(isAdmin)}>
            <Shield className={`w-4 h-4 ${isAdmin ? 'text-amber-300' : 'text-amber-500'}`} />
            {t('sidebar.admin')}
          </button>
        )}
        <button onClick={handleFeedback} data-testid="nav-feedback" className={navItemCls(false)}>
          <MessageSquare className="w-4 h-4 text-slate-500" />
          {t('sidebar.feedback')}
        </button>
      </nav>
      <div className="p-4 border-t border-slate-700/50">
        <div className="mb-3">
          <div className="flex items-center gap-2 text-sm text-slate-300 mb-0.5">
            <User className="w-3.5 h-3.5 text-slate-500" />
            <span className="truncate">{user?.contact_name || user?.email}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Building2 className="w-3 h-3" />
            <span className="truncate">{user?.company_name || '-'}</span>
          </div>
          <span className="inline-block mt-1 text-[10px] uppercase tracking-wider font-semibold text-[#4d8fd6]">
            {roleLabel}
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout}
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 px-2" data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> {t('sidebar.logout')}
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Mobile top bar — только < lg */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 h-12 bg-slate-900 text-white flex items-center justify-between px-3 shadow-md">
        <button onClick={() => setSidebarOpen(true)}
          className="w-10 h-10 -ml-1 rounded-sm hover:bg-slate-800 flex items-center justify-center"
          data-testid="sidebar-open" aria-label="Открыть меню">
          <Menu className="w-5 h-5" />
        </button>
        <span className="font-heading text-sm font-bold tracking-tight">{t('app.name')}</span>
        <button onClick={handleChat}
          className="w-10 h-10 -mr-1 rounded-sm hover:bg-slate-800 flex items-center justify-center relative"
          data-testid="mobile-chat-btn" aria-label="Чаты">
          <MessageCircle className="w-5 h-5" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[9px] font-bold rounded-full">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </div>

      {/* Mobile overlay-backdrop */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop" />
      )}

      {/* Sidebar:
          - lg+: статический left-rail (как было) шириной w-60
          - mobile: фиксированный off-canvas drawer, открывается слева,
            slide-in анимация transform translate-x */}
      <aside className={`bg-slate-900 text-white flex flex-col shrink-0
        lg:static lg:w-60 lg:translate-x-0
        fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw]
        transform transition-transform duration-200 ease-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar">
        {sidebarContent}
      </aside>

      {/* Main content area — отступ сверху для мобильного top-bar */}
      <main className="flex-1 overflow-auto bg-slate-50 pt-12 lg:pt-0">{children}</main>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}
